import { useCallback, useMemo } from 'react';
import useCurrentUser from '~/react-hooks/use-current-user';
import createPersistedState from '~/react-hooks/use-persisted-state';
import { Tag } from '~/types/models/tags';

export interface FilteredTags {
  filteredTags: Record<string, Tag>;
  getFilteredTagIds: () => string[];
  getFilteredTagCollection: () => Tag[];
  setSelectedTags: (tags: Tag[]) => void;
  clearSelectedTags: () => void;
}

export interface FilteredTagsProps {
  tags?: Tag[];
  clearTags?: () => void;
}

export const getTagNames = (tags: Tag[]) => {
  const tagsCount = tags.length;

  if (!tagsCount) {
    return '';
  }

  if (tagsCount === 1) {
    return tags[0].name;
  }

  const tagNames = tags.map((tag) => `'${tag.name}'`);
  const lastTagName = tagNames.pop();
  return `${tagNames.join(', ')} and ${lastTagName!}`;
};

const useFilteredTags = (userId?: string): FilteredTags => {
  const currentUser = useCurrentUser();

  const actualUserId = userId ?? currentUser.id;

  const useFilteredTags = useMemo(() => {
    return createPersistedState(`filtered-tags-${actualUserId}`);
  }, [actualUserId]);

  const [filteredTags, setFilteredTags] = useFilteredTags<Record<string, Tag>>({});
  const getFilteredTagIds = useCallback(() => Object.keys(filteredTags), [filteredTags]);
  const getFilteredTagCollection = useCallback(() => Object.values(filteredTags), [filteredTags]);

  const setSelectedTags = (tags: Tag[]) =>
    setFilteredTags(
      tags.reduce<Record<string, Tag>>(
        (tags, tag) => {
          return { ...tags, [tag.id]: tag };
        }, {}
      )
    );

  const clearSelectedTags = () => setFilteredTags({});

  return {
    filteredTags,
    getFilteredTagIds,
    getFilteredTagCollection,
    setSelectedTags,
    clearSelectedTags,
  };
};

export default useFilteredTags;
